'use client';

import DeliveryPlanPills from '@/app/dashboard/components/DeliveryPlanPills';
import styles from '@/app/orders/components/TrackingOrders.style';

import { OrderSearchResult, OrderSearchResultVM } from '@/core/entities/order-search-result';

import { AppColors, NoResults, PaginationControl, Tag } from '@cmbg-traky-web/design-system';
import { ConfigProvider, Skeleton, Table, TableProps, Tooltip } from 'antd';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { WebOrderError } from '@/core/errors/WebOrderError';
import { OrdersState } from '@/core/hooks/useOrdersState';
import { TableLoadingConfig } from '@/core/types/table-loading-config';
import {
  maskOrderId,
  trackClickInvoice,
  trackClickReturnIcon,
  trackOrderDetailOpen,
  trackPaginationChange
} from '@/core/utils/analytics.util';
import { SESSION_KEYS } from '@/core/utils/constants';
import { dayjsInstance } from '@/core/utils/date.util';
import { getAssociatedDocument } from '@/core/utils/order.util';
import { getStatusColor } from '@/core/utils/status.util';

export interface TrackingOrdersProps {
  ordersData?: OrderSearchResultVM;
  ordersState: OrdersState;
  updatePagination: (page: number, pageSize?: number) => void;
  loading: boolean;
  error: boolean | Error | null;
  setLoadingState: (state: { loading: boolean; error: boolean | Error | null }) => void;
  getTableLoadingConfig: () => boolean | TableLoadingConfig;
}

/**
 * This component displays a table of orders.
 *
 * @param {TrackingOrdersProps} props - The component props.
 *
 * @returns {JSX.Element} The rendered component.
 */
export function TrackingOrders({
  ordersData,
  ordersState,
  updatePagination,
  loading,
  error,
  setLoadingState
}: TrackingOrdersProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const pathname = usePathname();

  const [localCurrentPage, setLocalCurrentPage] = useState(ordersState.currentPage);
  const [viewportWidth, setViewportWidth] = useState<number>(1024);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    setViewportWidth(window.innerWidth);
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isTablet = mounted && viewportWidth >= 768 && viewportWidth <= 1024;
  const isMobile = mounted && viewportWidth < 768;

  useEffect(() => {
    setLocalCurrentPage(ordersState.currentPage);
  }, [ordersState.currentPage]);

  const [returnsByOrder, setReturnsByOrder] = useState<Record<string, boolean>>({});

  const resultCount = useMemo(() => {
    if (!ordersData) return 0;
    return ordersData.pagination?.total || ordersData.data?.length || 0;
  }, [ordersData]);

  const sourceScreen: 'search' | 'dashboard' = useMemo(() => {
    const p = pathname || '';
    return p.startsWith('/orders') ? 'search' : 'dashboard';
  }, [pathname]);

  const columns: TableProps<OrderSearchResult>['columns'] = useMemo(
    () => [
      {
        title: <span style={styles.columTitle}>{t('common:table-headers.order')}</span>,
        dataIndex: 'number',
        onCell: () => ({
          style: {
            whiteSpace: 'normal',
            wordBreak: 'normal',
            minWidth: 0,
            maxWidth: 260
          }
        }),
        render: (text, record, index) => {
          const associated = getAssociatedDocument(
            record as unknown as Record<string, unknown>,
            record.number,
            { docType: ordersState.docType, docId: ordersState.docId }
          );
          return (
            <button
              onMouseEnter={() => {
                router.prefetch(`/orders/${record.number}`);
              }}
              onClick={(e) => {
                e.stopPropagation();

                sessionStorage.setItem(SESSION_KEYS.SEARCHBAR_BACKBUTTON, 'true');

                try {
                  const thisRowAssociated = getAssociatedDocument(
                    record as unknown as Record<string, unknown>,
                    record.number,
                    { docType: ordersState.docType, docId: ordersState.docId }
                  );
                  const payload: Record<string, unknown> = {
                    docType: ordersState.docType,
                    docId: ordersState.docId,
                    at: Date.now()
                  };
                  if (thisRowAssociated) {
                    payload.associatedId = thisRowAssociated.id;
                    payload.associatedKind = thisRowAssociated.kind;
                    payload.associatedLabel = thisRowAssociated.label;
                  }
                  sessionStorage.setItem(SESSION_KEYS.LAST_SEARCH_DOC, JSON.stringify(payload));
                } catch {
                  /* ignore */
                }

                void trackClickInvoice({
                  invoice_id: String(record.number ?? ''),
                  doc_type: 'invoice',
                  source_screen: sourceScreen,
                  result_position: typeof index === 'number' ? index + 1 : null,
                  result_count: resultCount || null,
                  status: record.status?.name ?? null,
                  delivery_plan:
                    record.deliveryPlanTypes && record.deliveryPlanTypes[0]
                      ? record.deliveryPlanTypes[0].id || record.deliveryPlanTypes[0].name || null
                      : null
                });

                trackOrderDetailOpen({
                  orderIdMasked: maskOrderId(String(record.number)),
                  deliveryPlanType:
                    record.deliveryPlanTypes && record.deliveryPlanTypes[0]
                      ? record.deliveryPlanTypes[0].id || record.deliveryPlanTypes[0].name || null
                      : undefined
                });
                setTimeout(() => {
                  router.push(`/orders/${record.number}`);
                }, 100);
              }}
              style={{
                cursor: 'pointer',
                color: 'inherit',
                background: 'none',
                border: 'none',
                padding: 0,
                textAlign: 'left',
                fontFamily: 'inherit',
                fontSize: 'inherit',
                width: '100%',
                minWidth: 0
              }}
              aria-label={`Ver detalles del pedido ${text}`}>
              <div style={styles.invoiceCell}>
                <span
                  style={{
                    ...styles.invoiceCellLabel,
                    color: AppColors.VA.PRIMARY._90_
                  }}>
                  {text}
                </span>
                <span style={{ ...styles.invoiceCellMeta, ...styles.sub }}>
                  <i
                    className='bi bi-calendar3'
                    style={{ fontSize: 11, color: AppColors.VA.NEUTRALS._50_ }}
                  />
                  {dayjsInstance(record.date).format('DD-MM-YYYY, hh:mm a')}
                </span>
                {associated && (
                  <div
                    style={styles.associationBadge}
                    title={`Asociada a la ${associated.label} ${associated.id}`}>
                    <i
                      className='bi bi-link-45deg'
                      style={{
                        fontSize: 10,
                        color: AppColors.VA.PRIMARY._50_,
                        flexShrink: 0
                      }}
                    />
                    <span style={styles.associationText}>Asociada a la {associated.label}</span>
                    <span style={styles.associationDocId}>{associated.id}</span>
                  </div>
                )}
              </div>
            </button>
          );
        }
      },
      {
        title: t('common:table-headers.client'),
        width: 230,
        render: (_, record) => (
          <>
            <div style={styles.customerName}>{record.customer?.name}</div>
            <span style={styles.sub}>{record.customer?.id}</span>
          </>
        )
      },
      {
        title: t('common:table-headers.delivery-store'),
        render: (_, record) => {
          const dsName =
            (typeof record.deliveryStore === 'string'
              ? record.deliveryStore
              : record.deliveryStore?.name) ||
            (typeof record.store === 'string' ? record.store : record.store?.name) ||
            (typeof record.originStore === 'string'
              ? record.originStore
              : record.originStore?.name) ||
            undefined;

          const dsOrgId =
            (record.deliveryStore && typeof record.deliveryStore === 'object'
              ? record.deliveryStore.organizationId
              : undefined) ||
            (record.store && typeof record.store === 'object'
              ? record.store.organizationId
              : undefined) ||
            (record.originStore && typeof record.originStore === 'object'
              ? record.originStore.organizationId
              : undefined);

          return (
            <>
              {dsName}
              <br />
              <span style={styles.sub}>{dsOrgId}</span>
            </>
          );
        }
      },
      {
        title: <span style={styles.columTitle}>{t('common:table-headers.promise-date')}</span>,
        dataIndex: 'deliveryPromiseDate',
        render: (d: string | null) => {
          if (!d) {
            return '-';
          }
          const formattedDate = dayjsInstance(d).format('ddd DD MMMM YYYY').replace('.', '');
          const capitalizedDate = formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
          return (
            <>
              {capitalizedDate}
              <br />
              <span style={styles.sub}>{dayjsInstance(d).format('hh:mm A')}</span>
            </>
          );
        }
      },
      {
        title: t('common:table-headers.delivery-plan'),
        render: (_, record) => <DeliveryPlanPills deliveryPlans={record.deliveryPlanTypes || []} />
      },
      { title: t('common:table-headers.products'), dataIndex: 'productsQty', align: 'start' },
      {
        title: t('common:table-headers.status'),
        dataIndex: 'status',
        align: 'start',
        render: (_, record) => {
          const status = record.status;
          return status ? <Tag type={getStatusColor(status)}>{status.name}</Tag> : null;
        }
      },
      {
        title: t('common:status.return'),
        key: 'returns',
        align: 'center',
        width: 100,
        render: (_, record) => {
          const invoiceId = record.number ? String(record.number) : '';
          const qty = typeof record.returnsQty === 'number' ? record.returnsQty : undefined;
          const hasReturnActive =
            qty != null
              ? qty > 0
              : invoiceId && returnsByOrder.hasOwnProperty(invoiceId)
                ? returnsByOrder[invoiceId]
                : false;
          const src = hasReturnActive
            ? 'https://iili.io/fbgaxxp.png'
            : 'https://iili.io/q2rs0pp.png';
          if (hasReturnActive && invoiceId) {
            return (
              <Tooltip title='Factura con devolución'>
                <button
                  onMouseEnter={() => {
                    router.prefetch(`/orders/${invoiceId}/returned`);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    void trackClickReturnIcon({
                      docId: invoiceId,
                      docType: 'factura',
                      hasReturn: true,
                      status: record.status?.name ?? null
                    });
                    router.push(`/orders/${invoiceId}/returned`);
                  }}
                  aria-label={t('orders:buttons.returns')}
                  style={{
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                    padding: 0
                  }}>
                  <Image
                    src={src}
                    alt={t('orders:buttons.returns')}
                    width={18}
                    height={18}
                    unoptimized={true}
                  />
                </button>
              </Tooltip>
            );
          }
          return (
            <Tooltip title='Factura con devolución'>
              <Image
                src={src}
                alt={hasReturnActive ? 'Devolución' : 'Sin devolución'}
                width={18}
                height={18}
                unoptimized={true}
              />
            </Tooltip>
          );
        }
      },
      {
        title: t('common:table-headers.total'),
        dataIndex: 'total',
        align: 'right',
        render: (v: number) =>
          v.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            currencyDisplay: 'narrowSymbol'
          })
      }
    ],
    [t, router, returnsByOrder, resultCount, sourceScreen, ordersState.docType, ordersState.docId]
  );

  const handlePageChange = (page: number) => {
    if (page === localCurrentPage) {
      return;
    }

    setLocalCurrentPage(page);
    setLoadingState({ loading: true, error: null });
    trackPaginationChange({ pageNumber: page });
    updatePagination(page);
  };

  const handleInternalPageSizeChange = useCallback(
    (size: number) => {
      if (size === ordersState.pageSize) {
        return;
      }

      setLocalCurrentPage(1);
      setLoadingState({ loading: true, error: null });
      updatePagination(1, size);
    },
    [ordersState.pageSize, setLoadingState, updatePagination]
  );

  const effectiveData = useMemo(() => {
    const ds = ordersData?.data ?? ([] as OrderSearchResult[]);
    const page = ordersData?.pagination.page ?? 1;
    const limit = ordersData?.pagination.limit ?? ordersState.pageSize;

    const deriveStableKey = (item: OrderSearchResult): string => {
      if (item.number) return String(item.number);
      return `${item.customer?.id ?? 'no-cust'}-${item.date ?? ''}`;
    };

    const bestByKey = new Map<string, OrderSearchResult>();

    const hasDeliveryInfo = (it: OrderSearchResult) => {
      const ds2 = it.deliveryStore ?? it.store ?? it.originStore;
      const hasStore = !!ds2?.id || !!ds2?.name;
      const hasPromise = !!it.deliveryPromiseDate;
      const hasTypes = !!it.deliveryPlanTypes && it.deliveryPlanTypes.length > 0;
      const hasQty = typeof it.productsQty === 'number' && it.productsQty > 0;
      return Number(hasStore) + Number(hasPromise) + Number(hasTypes) + Number(hasQty);
    };

    for (const item of ds) {
      const key = deriveStableKey(item);
      const prev = bestByKey.get(key);
      if (!prev) {
        bestByKey.set(key, item);
      } else {
        const prevScore = hasDeliveryInfo(prev);
        const itemScore = hasDeliveryInfo(item);
        if (itemScore > prevScore) {
          bestByKey.set(key, item);
        }
      }
    }

    const dsUnique = Array.from(bestByKey.values());

    if (dsUnique.length <= limit) {
      return dsUnique;
    }

    const startIndex = (page - 1) * limit;

    if (startIndex >= dsUnique.length) {
      const tailStart = Math.max(0, dsUnique.length - limit);
      return dsUnique.slice(tailStart, dsUnique.length);
    }

    const endIndex = Math.min(startIndex + limit, dsUnique.length);
    return dsUnique.slice(startIndex, endIndex);
  }, [ordersData, ordersState.pageSize]);

  const onRow = useCallback(
    (record: OrderSearchResult) => ({
      onClick: () => {
        sessionStorage.setItem(SESSION_KEYS.SEARCHBAR_BACKBUTTON, 'true');
        const position =
          effectiveData.findIndex((it) =>
            it.number ? String(it.number) === String(record.number) : false
          ) + 1;
        void trackClickInvoice({
          invoice_id: String(record.number ?? ''),
          doc_type: 'invoice',
          source_screen: pathname?.startsWith('/orders') ? 'search' : 'dashboard',
          result_position: position > 0 ? position : null,
          result_count: resultCount || null,
          status: record.status?.name ?? null,
          delivery_plan:
            record.deliveryPlanTypes && record.deliveryPlanTypes[0]
              ? record.deliveryPlanTypes[0].id || record.deliveryPlanTypes[0].name || null
              : null
        });
        trackOrderDetailOpen({
          orderIdMasked: maskOrderId(String(record.number)),
          deliveryPlanType:
            record.deliveryPlanTypes && record.deliveryPlanTypes[0]
              ? record.deliveryPlanTypes[0].id || record.deliveryPlanTypes[0].name || null
              : undefined
        });
        setTimeout(() => {
          router.push(`/orders/${record.number}`);
        }, 100);
      },
      onMouseEnter: () => {
        router.prefetch(`/orders/${record.number}`);
      },
      style: { cursor: 'pointer' },
      role: 'button',
      tabIndex: 0,
      'aria-label': t('common:look-order-detail', { total: record.number })
    }),
    [router, t, effectiveData, pathname, resultCount]
  );

  const isLoading = loading;

  const skeletonColumns: TableProps<OrderSearchResult>['columns'] = useMemo(
    () =>
      columns.map((col, idx) => ({
        ...col,
        render: () =>
          idx < 4 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Skeleton.Button active style={{ width: '80%', height: 16 }} />
              <Skeleton.Button active style={{ width: '60%', height: 12 }} />
            </div>
          ) : (
            <Skeleton.Button
              active
              style={{ width: col.align === 'right' ? '40%' : '80%', height: 16 }}
            />
          )
      })),
    [columns]
  );

  const skeletonData = useMemo(
    () =>
      Array.from({ length: ordersState.pageSize || 10 }).map((_, i) => ({
        id: `s-${i}`
      })) as unknown as OrderSearchResult[],
    [ordersState.pageSize]
  );

  useEffect(() => {
    const map = ordersData?.returnsByOrder;
    if (map && typeof map === 'object') {
      setReturnsByOrder(map);
    } else {
      setReturnsByOrder({});
    }
  }, [ordersData]);

  useEffect(() => {}, [
    isLoading,
    error,
    ordersData,
    ordersState.currentPage,
    ordersState.pageSize,
    effectiveData
  ]);
  return (
    <ConfigProvider
      theme={{
        components: {
          Table: {
            headerBg: AppColors.VA.BACKGROUND._05_,
            headerSortHoverBg: AppColors.VA.BACKGROUND._05_,
            headerSortActiveBg: AppColors.VA.BACKGROUND._05_,
            headerColor: AppColors.VA.NEUTRALS._90_,
            headerBorderRadius: 0,
            headerSplitColor: 'transparent',
            borderColor: AppColors.VA.BACKGROUND._20_
          }
        }
      }}>
      {isLoading ? (
        <>
          <div
            style={
              isTablet
                ? styles.tableContainerTablet
                : isMobile
                  ? styles.tableContainerMobile
                  : undefined
            }>
            <Table<OrderSearchResult>
              rowKey={(r) =>
                r.number ? String(r.number) : r.id ? String(r.id) : `s-${Math.random()}`
              }
              dataSource={skeletonData}
              columns={skeletonColumns}
              pagination={false}
              style={
                isTablet
                  ? styles.ordersTableTablet
                  : isMobile
                    ? styles.ordersTableMobile
                    : styles.ordersTable
              }
              scroll={isTablet || isMobile ? { x: 'max-content' } : undefined}
              tableLayout={'auto'}
              locale={{ emptyText: '' }}
            />
          </div>
        </>
      ) : (
        <>
          {(((error instanceof Error || error === true) &&
            (!ordersData || ordersData.data.length === 0)) ||
            !!ordersData?.noResultsConfig) && (
            <NoResults
              title={
                ordersData?.noResultsConfig?.title ||
                (error instanceof WebOrderError && error.type === 'no-results'
                  ? `El pedido "${error.docId}" aún no ha sido facturado`
                  : undefined)
              }
              description={
                ordersData?.noResultsConfig?.description ||
                (error instanceof WebOrderError ? error.message : undefined)
              }
              allowHtml={error instanceof WebOrderError}
              iconClass={
                ordersData?.noResultsConfig?.iconClass ||
                (error instanceof WebOrderError ? error.customIcon : undefined)
              }
              iconColor={ordersData?.noResultsConfig?.iconColor}
            />
          )}

          {!ordersData?.noResultsConfig && (
            <div
              style={
                isTablet
                  ? styles.tableContainerTablet
                  : isMobile
                    ? styles.tableContainerMobile
                    : undefined
              }>
              <Table<OrderSearchResult>
                onRow={onRow}
                rowKey={(r) =>
                  r.number ? String(r.number) : r.id ? String(r.id) : `s-${Math.random()}`
                }
                dataSource={ordersData ? effectiveData : []}
                columns={columns}
                pagination={false}
                style={
                  isTablet
                    ? styles.ordersTableTablet
                    : isMobile
                      ? styles.ordersTableMobile
                      : styles.ordersTable
                }
                scroll={isTablet || isMobile ? { x: 'max-content' } : undefined}
                tableLayout={'auto'}
              />
            </div>
          )}

          {ordersData &&
            !ordersData.noResultsConfig &&
            (ordersData.pagination.total > ordersState.pageSize ||
              ordersData.data.length >= ordersState.pageSize) && (
              <footer
                style={
                  isTablet
                    ? styles.footerBarTablet
                    : isMobile
                      ? styles.footerBarMobile
                      : styles.footerBar
                }>
                <PaginationControl
                  current={localCurrentPage}
                  pageSize={ordersState.pageSize}
                  totalItems={ordersData.pagination.total}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handleInternalPageSizeChange}
                />
              </footer>
            )}
        </>
      )}
    </ConfigProvider>
  );
}

export default TrackingOrders;
